import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import traverse from 'traverse';
import cloneDeep from 'lodash/cloneDeep';
import first from 'lodash/first';
import get from 'lodash/get';
import sortBy from 'lodash/sortBy';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import toLower from 'lodash/toLower';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from '@mui/material/styles/styled';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Typography from '@mui/material/Typography';
import { TreeItem, treeItemClasses } from '@mui/x-tree-view/TreeItem';
import { TreeView } from '@mui/x-tree-view/TreeView';

import { navigate } from '../../../store/pages';
import useNodes from '../../../store/hooks/useNodes';

const Label = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

const TreeItemStyled = styled(TreeItem)(({ theme }) => ({
  [`& .${treeItemClasses.root}`]: {
    color: theme.palette.text.secondary,
    '&:hover > $content': {
      backgroundColor: theme.palette.action.hover,
    },
    '&$selected > $content': {
      backgroundColor: `var(--tree-view-bg-color, ${theme.palette.grey[400]})`,
      color: 'var(--tree-view-color)',
    },
    '&:focus > $content $label, &:hover > $content $label, &$selected > $content $label':
      {
        backgroundColor: 'transparent',
      },
    '&$selected > $content $label:hover, &$selected:focus > $content $label': {
      backgroundColor: 'transparent',

      // Reset on touch devices, it doesn't add specificity
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
  [`& .${treeItemClasses.group}`]: {
    '& $content': {
      paddingLeft: 18,
    },
  },
  [`& .${treeItemClasses.content}`]: {
    color: theme.palette.text.secondary,
    borderTopRightRadius: theme.spacing(2),
    borderBottomRightRadius: theme.spacing(2),
    paddingRight: theme.spacing(1),
    fontWeight: theme.typography.fontWeightMedium,
    '$expanded > &': {
      fontWeight: theme.typography.fontWeightRegular,
    },
  },
  [`& .${treeItemClasses.expanded}`]: {},
}));

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  marginLeft: theme.spacing(1),
  width: '100%',
  padding: 1,
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    paddingLeft: theme.spacing(2),
    transition: theme.transitions.create('width'),
    width: '100%',
    padding: 0,
  },
}));

const findNode = (nodes, key, id) => {
  return first(
    traverse(nodes).reduce(function (acc, node) {
      return this.notLeaf && node[key] === id
        ? acc.concat({ ...node, level: this.level })
        : acc;
    }, [])
  );
};

const findAllNodes = (text, items) => {
  var i = 0,
    found,
    result = [];

  for (; i < items.length; i++) {
    const label = get(items, `${i}.label`);
    if (toLower(label).includes(toLower(text))) {
      result.push(items[i]);
    } else if (isArray(items[i].children)) {
      found = findAllNodes(text, items[i].children);
      if (found.length) {
        result = result.concat(found);
      }
    }
  }
  return uniqBy(result, 'id');
};

const updateExpanded = (_expanded, user, location, pages) => {
  let expanded = cloneDeep(_expanded);
  expanded = uniq(expanded);

  if (
    user?.default_organization &&
    !includes(expanded, user.default_organization)
  ) {
    expanded.push(user.default_organization);
  }

  const page = location?.pathname?.replace('/app/', '');
  const pageRelationId = get(pages, `${page}.id`);

  if (!includes(pageRelationId)) {
    expanded.push(pageRelationId);
  }
  return expanded;
};

export default function NavTree() {
  const dispatch = useDispatch();
  const nodes = useNodes();

  const user = useSelector((state) => state.user.item);
  const pages = useSelector((state) => state.pages);
  const location = useLocation();

  const [searchText, setSearchText] = useState('');
  const [treeNodes, setTreeNodes] = useState([]);
  const [expanded, setExpanded] = useState([]);
  const [selected, setSelected] = useState([]);

  const rowHeight = '25px';

  useEffect(() => {
    if (!isEmpty(nodes)) {
      if (searchText === '') {
        setTreeNodes(nodes);
      } else {
        setTreeNodes(findAllNodes(searchText, nodes));
      }
    }
  }, [nodes, searchText]);

  useEffect(() => {
    if (user && location) {
      setExpanded((e) => updateExpanded(e, user, location, pages));
    }
  }, [user, location, treeNodes, pages]);

  const handleLabelClick = (nodeId) => {
    setSelected([nodeId]);
    let node = findNode(nodes, 'id', nodeId);
    dispatch(
      navigate({
        page: node.resourceType,
        id: nodeId,
      })
    );
  };

  const handleToggle = (e, nodeIds) => {
    if (
      e.target.id !== 'tree-item-label' &&
      !e.target.hasAttribute('fill') &&
      !e.target.hasAttribute('type')
    ) {
      setExpanded(nodeIds);
    }
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const mapNodes = (_nodes) => {
    return _nodes.map((node) => {
      const { children, id, hide, icon, label, level } = node;
      const _children = sortBy(children, [
        (child) => {
          return child.type.includes('-sensor');
        },
        (child) => {
          return child.type.includes('-inverter');
        },
        (child) => {
          return child.type.includes('-meter');
        },
      ]);

      const maxWidth = 280 - level * 10;
      return (
        <TreeItemStyled
          id='tree-item-label'
          key={id}
          nodeId={id}
          label={
            <Label
              sx={{ height: rowHeight }}
              id='tree-item-label'
              onClick={() => handleLabelClick(id)}>
              {icon ? (
                <IconButton size='small' sx={{ p: 0, pr: 1 }}>
                  <FontAwesomeIcon id='tree-item-label' icon={icon} size='xs' />
                </IconButton>
              ) : (
                <></>
              )}
              <Typography
                id='tree-item-label'
                variant='body2'
                align='left'
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: '1',
                  WebkitBoxOrient: 'vertical',
                  maxWidth,
                }}>
                {label}
              </Typography>
            </Label>
          }
          style={
            hide
              ? { display: 'none' }
              : {
                  '--tree-view-color': get(node, 'color', '#1a73e8'),
                  '--tree-view-bg-color': get(node, 'bgColor', '#e8f0fe'),
                }
          }>
          {_children && mapNodes(_children)}
        </TreeItemStyled>
      );
    });
  };

  return (
    <>
      <Search>
        <SearchIconWrapper>
          <FontAwesomeIcon icon={['fal', 'search']} />
        </SearchIconWrapper>
        <StyledInputBase
          placeholder='Search…'
          value={searchText}
          onChange={handleSearchChange}
          sx={{ width: '100%', pl: 4 }}
        />
      </Search>
      <Divider />
      <TreeView
        defaultCollapseIcon={<FontAwesomeIcon icon={['fal', 'caret-down']} />}
        defaultExpandIcon={<FontAwesomeIcon icon={['fal', 'caret-right']} />}
        expanded={expanded}
        onNodeToggle={handleToggle}
        selected={selected}>
        {mapNodes(treeNodes)}
      </TreeView>
    </>
  );
}
